/**
 * Swup page transition initialization
 *
 * @package TotalDigital
 */

import SwupA11yPlugin from '@swup/a11y-plugin';
import SwupBodyClassPlugin from '@swup/body-class-plugin';
import SwupHeadPlugin from '@swup/head-plugin';
import SwupSlideTheme from '@swup/slide-theme';
import Swup from 'swup';


import { getLenis } from './lenis.js';

// import SwupGtmPlugin from '@swup/gtm-plugin';
// import SwupGaPlugin from '@swup/ga-plugin';

let swup = null;

const isAdmin = () => {
	if (typeof window === 'undefined') {
		return false;
	}

	const url = window.location.href;
	const pathname = window.location.pathname;

	if (pathname.includes('/wp-admin/') || pathname.includes('/wp-login.php')) {
		return true;
	}

	if (document.body && document.body.classList.contains('wp-admin')) {
		return true;
	}

	if (window.self !== window.top) {
		return true;
	}

	return false;
};

const isDevMode = () => {
	if (typeof window === 'undefined') {
		return false;
	}

	// Check if Vite dev server is running (HMR connection or dev scripts)
	const hasViteHMR = document.querySelector('script[src*=":3001"]') !== null;
	const hasViteClient = document.querySelector('script[src*="@vite/client"]') !== null;
	const hasViteDevScript = Array.from(document.querySelectorAll('script')).some(
		(script) => script.src && (script.src.includes(':3001') || script.src.includes('@vite'))
	);

	return hasViteHMR || hasViteClient || hasViteDevScript;
};

const initSwup = () => {
	if (isAdmin()) {
		return;
	}

	const mainContainer = document.querySelector('#main');
	if (!mainContainer) {
		console.warn('Swup: #main container not found, retrying...');
		setTimeout(initSwup, 100);
		return;
	}

	swup = new Swup({
		containers: ['#main'],
		linkSelector:
			'a[href]:not([data-no-swup]):not([href^="#"]):not([href^="mailto:"]):not([href^="tel:"]):not([href^="javascript:"]):not([target="_blank"]):not([href*="/wp-admin"]):not([href*="/wp-login"])',
		animationSelector: '.swup-transition-main',
		plugins: [
			new SwupSlideTheme({
				mainElement: '#main',
				reversed: true,
			}),
			new SwupA11yPlugin({
				headingSelector: 'h1',
				respectReducedMotion: true,
				autofocus: false,
			}),
			new SwupBodyClassPlugin(),
			new SwupHeadPlugin({
				attributes: ['lang', 'dir'],
				persistAssets: true,
				awaitAssets: isDevMode() ? false : true,
			}),
			// new SwupGtmPlugin(),
			// new SwupGaPlugin(),
		],
		cache: true,
		preload: true,
		skipPopStateHandling: (event) => false,
		animateHistoryBrowsing: true,
		// Pages that embed the PR contact form load a chain of scripts (the form
		// JS, validation, datepicker, reCAPTCHA) that WordPress only enqueues on
		// those pages. Swup swaps #main without pulling in those footer scripts,
		// so after a Swup visit the form has no submit handler and falls back to
		// a native page reload. Force a normal full page load for these paths so
		// every form script is present and the form works as designed.
		ignoreVisit: (url, { el } = {}) => {
			const fullReloadPaths = ['/contact-us'];

			try {
				const path = new URL(url, window.location.origin).pathname;
				if (fullReloadPaths.some((p) => path.startsWith(p))) {
					return true;
				}
			} catch (e) {
				// Ignore URL parsing errors and fall through.
			}

			// Preserve Swup's default opt-out via data-no-swup.
			return Boolean(el && el.closest && el.closest('[data-no-swup]'));
		},
	});

	swup.hooks.on('visit:start', () => {
		if (typeof window.closeMenu === 'function') {
			window.closeMenu();
		}

		const activeElement = document.activeElement;
		if (activeElement && activeElement !== document.body && activeElement.blur) {
			activeElement.blur();
		}

		document.dispatchEvent(new CustomEvent('swup:visit:start'));
	});

	swup.hooks.on('animation:out:start', () => {
		// Remove "dark" class from body before sliding content down
		if (document.body.classList.contains('dark')) {
			document.body.classList.remove('dark');
		}

		// Scroll to top immediately when transition starts, before new page loads
		const lenis = getLenis();
		if (lenis && typeof lenis.scrollTo === 'function') {
			lenis.scrollTo(0, { immediate: true });
		} else {
			window.scrollTo(0, 0);
		}

		// Toggle opacity class on header notice before transition starts
		const headerNotice = document.querySelector('.header-notice');
		if (headerNotice) {
			headerNotice.classList.add('opacity', 'animating');
		}

		const mainContainer = document.querySelector('#main');
		if (mainContainer) {
			const focusableElements = mainContainer.querySelectorAll(
				'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
			);
			focusableElements.forEach((el) => {
				if (el === document.activeElement) {
					el.blur();
				}
				const currentTabindex = el.getAttribute('tabindex');
				if (currentTabindex !== '-1') {
					el.setAttribute('data-original-tabindex', currentTabindex || '0');
					el.setAttribute('tabindex', '-1');
				}
			});
		}
	});

	swup.hooks.on('animation:out:end', () => {
		document.body.classList.add('bgs', 'bg-GL8');
	});

	swup.hooks.on('content:replace', async () => {
		// Reset header state
		const siteHeader = document.querySelector('.site-header');
		if (siteHeader) {
			// Always remove sticky class and clear transforms
			if (siteHeader.classList.contains('is-sticky')) {
				siteHeader.style.transform = '';
				siteHeader.classList.remove('is-sticky');
			}
		}

		// eslint-disable-next-line no-undef
		const event = new CustomEvent('swup:content:replace');
		document.dispatchEvent(event);
	});

	swup.hooks.on('animation:in:start', () => {
		document.body.classList.remove('bgs', 'bg-GL8');
	});

	swup.hooks.on('animation:in:end', () => {
		const mainContainer = document.querySelector('#main');
		if (mainContainer) {
			const focusableElements = mainContainer.querySelectorAll('[tabindex="-1"]');
			focusableElements.forEach((el) => {
				const originalTabindex = el.getAttribute('data-original-tabindex');
				if (originalTabindex !== null) {
					el.setAttribute('tabindex', originalTabindex);
					el.removeAttribute('data-original-tabindex');
				} else {
					el.removeAttribute('tabindex');
				}
			});
		}

		// Remove opacity class on header notice after transition ends
		const headerNotice = document.querySelector('.header-notice');
		if (headerNotice) {
			headerNotice.classList.remove('opacity', 'animating');
		}

		// Ensure scroll is at top (in case it wasn't already)
		const lenis = getLenis();
		if (lenis && typeof lenis.scrollTo === 'function') {
			lenis.scrollTo(0, { immediate: true });
		} else {
			window.scrollTo(0, 0);
		}

	});

	swup.hooks.on('page:view', () => {
		if (typeof window.Cookiebot !== 'undefined' && window.Cookiebot.consent) {
			window.Cookiebot.consent.update();
		}

		if (typeof window.dataLayer !== 'undefined' && window.dataLayer) {
			window.dataLayer.push({
				event: 'cookie_consent_update',
			});
		}
	});
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initSwup);
} else {
	initSwup();
}

export default swup;
